'use client'

import { memo } from 'react'
import { X } from 'lucide-react'
import RoomTypeBanner from '../../shared/room-type-banner'
import type { RoomType } from '../../shared/type'
import { AnimatePresence, motion } from 'framer-motion'
import { TruncatedText } from '../../../ui/truncated-text'
import { Image } from '../../../ui/image'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { TruncatedHTML } from '~/components/ui/truncated-html'

interface RoomDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  room: RoomType | null
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const modalVariants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      damping: 25,
      stiffness: 300,
    },
  },
  exit: {
    y: '100%',
    opacity: 0,
    transition: {
      type: 'spring' as const,
      damping: 25,
      stiffness: 300,
    },
  },
}

function RoomDetailsModal({ isOpen, onClose, room }: RoomDetailsModalProps) {
  if (!room) return null

  const { locale } = useLocale()
  const t = getTranslation(locale)

  const allImages = [room.imageUrl, ...room.images.map((img) => img.imageUrl)].filter(
    (url) => url && url.trim() !== ''
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/50"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto bg-white"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-4">
                <div>
                  <h3 className="font-lora text-[18px] font-medium text-secondary-950">
                    {t('roomType.details')}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 transition-colors hover:text-gray-500"
                >
                  <X className="h-6 w-6 text-secondary-950" />
                </button>
              </div>

              {/* Content */}
              <div className="h-[300px] w-full">
                <RoomTypeBanner images={allImages} showSlider={true} />
              </div>

              <div className="p-4">
                <h2 className="mb-2 font-lora text-xl font-medium text-gray-900">
                  {room.roomTypeName}
                </h2>

                <TruncatedHTML html={room.roomDescription} />

                <div className="mb-6">
                  <h3 className="mb-3 font-medium text-gray-900">{t('roomType.amenities')}</h3>
                  <div className="flex flex-wrap gap-3">
                    {room.roomServices.map((service, index) => (
                      <div
                        key={index}
                        className="flex min-w-fit items-center gap-2 whitespace-nowrap rounded-[4px] bg-[#F8F4F0] p-2 px-3"
                      >
                        <Image
                          src={service.icon || '/static/icons/check-circle.svg'}
                          alt={service.serviceName}
                          fill
                          aspectRatio={'1/1'}
                          objectFit={'contain'}
                          loading="lazy"
                          className={`h-5 w-5`}
                        />
                        <span className="text-sm text-secondary-950">{service.serviceName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function arePropsEqual(prevProps: RoomDetailsModalProps, nextProps: RoomDetailsModalProps) {
  // Check if isOpen changed
  if (prevProps.isOpen !== nextProps.isOpen) return false

  // If both rooms are null, props are equal
  if (!prevProps.room && !nextProps.room) return true

  // If one room is null and the other isn't, props are not equal
  if (!prevProps.room || !nextProps.room) return false

  // At this point, we know both rooms are not null
  const prevRoom = prevProps.room as RoomType
  const nextRoom = nextProps.room as RoomType

  // Compare essential room properties
  return (
    prevRoom.roomTypeName === nextRoom.roomTypeName &&
    prevRoom.roomDescription === nextRoom.roomDescription &&
    prevRoom.imageUrl === nextRoom.imageUrl &&
    // Compare room services length and content
    prevRoom.roomServices.length === nextRoom.roomServices.length &&
    prevRoom.roomServices.every(
      (service, index) =>
        service.serviceName === nextRoom.roomServices[index].serviceName &&
        service.icon === nextRoom.roomServices[index].icon
    ) &&
    // Compare images array
    prevRoom.images.length === nextRoom.images.length &&
    prevRoom.images.every((img, index) => img.imageUrl === nextRoom.images[index].imageUrl)
  )
}

export default memo(RoomDetailsModal, arePropsEqual)
